// dependency-graph-ignore-file

import { Box, Stack, Text } from '@atlaskit/primitives';
import { analyticsClient, getDatabaseContainerType } from '@databases/analytics';
import {
  InlineRovoPromptTriggerSource,
  openInlineRovoPrompt
} from '@databases/database-ai-first-creation-state/inline-rovo-prompt-state';
import { getFieldType } from '@databases/fields';
import {
  DatabaseTraceAction,
  DatabaseTraceSource,
  traceDatabaseAction
} from '@databases/frontend-metrics/perf-tracing';
import { type MessageFormatter } from '@databases/intl/intl-types';
import { messages } from '@databases/intl/messages';
import {
  doClearEntireFieldsWithIds,
  doDeleteFieldsWithIds,
  doDuplicateEntireFieldsWithIds,
  doInsertFieldsWithIds,
  doMoveFieldsWithIds,
  doSelectNewFields
} from '@databases/redux/contextual-menu/actions';
import { appStore } from '@databases/redux/store';
import {
  type SelectionsApiContract,
  type DatabaseAdapter
} from '@databases/selections-api-types';
import { type DatabaseField, DatabaseFieldType } from '@databases/types/database';
import { type DatabaseView } from '@databases/types/database/view';
import {
  type ViewChangeType,
  ViewChangeTypes
} from '@databases/utils/database/view/view';
import { afterNextLayout } from '@databases/utils/lib/raf';
import React from 'react';

import { MetricName, ReliabilityMetrics } from '@canvas/frontend-metrics';

import { type ColumnActionsAPI, type PositionType } from '../types.ts';
import { getSelectedFieldIds } from '../utils/resolve-column-selection.ts';

/**
 * Builds the action handlers for field-level operations in the action bar.
 */
export const buildColumnActions = ({
  contentId,
  selectionsApi,
  view,
  databaseAdapter,
  setViewChange,
  formatMessage
}: {
  contentId: string;
  selectionsApi: SelectionsApiContract;
  view: DatabaseView;
  databaseAdapter: DatabaseAdapter;
  setViewChange: (view: ViewChangeType) => void;
  formatMessage: MessageFormatter;
}): ColumnActionsAPI => {
  const dispatch = appStore.dispatch;

  /**
   * Duplicates all selected fields and selects the newly created copies.
   */
  const handleDuplicateFields = () => {
    const fieldIds = getSelectedFieldIds(selectionsApi.getResolvedSelection());

    if (fieldIds.length === 0) {
      return;
    }

    const beforeFields = new Set(databaseAdapter.getVisibleFieldIds());

    traceDatabaseAction(
      {
        action: DatabaseTraceAction.FieldDuplicate,
        source: DatabaseTraceSource.ActionBar
      },
      () => {
        dispatch(doDuplicateEntireFieldsWithIds(contentId, view.id, fieldIds));
      }
    );

    const afterFields = databaseAdapter.getVisibleFieldIds();
    const newFieldIds = afterFields.filter((id) => !beforeFields.has(id));

    if (newFieldIds.length > 0) {
      selectionsApi.selectFields(newFieldIds);
    } else {
      selectionsApi.deselectAll();
    }
  };

  /**
   * Clears the content of all selected fields without deleting them.
   */
  const handleClearFields = () => {
    const fieldIds = getSelectedFieldIds(selectionsApi.getResolvedSelection());

    if (fieldIds.length === 0) {
      return;
    }

    traceDatabaseAction(
      { action: DatabaseTraceAction.FieldClear, source: DatabaseTraceSource.ActionBar },
      () => {
        dispatch(doClearEntireFieldsWithIds(contentId, fieldIds));
      }
    );
  };

  /**
   * Moves the selected fields one position to the left.
   */
  const handleMoveFieldsLeft = () => {
    const fieldIds = getSelectedFieldIds(selectionsApi.getResolvedSelection());

    if (fieldIds.length === 0) {
      return;
    }

    traceDatabaseAction(
      {
        action: DatabaseTraceAction.FieldMoveLeft,
        source: DatabaseTraceSource.ActionBar
      },
      () => {
        dispatch(doMoveFieldsWithIds(contentId, view.settings, true, fieldIds));
      }
    );

    afterNextLayout(() => selectionsApi.selectFields(fieldIds));
  };

  /**
   * Moves the selected fields one position to the right.
   */
  const handleMoveFieldsRight = () => {
    const fieldIds = getSelectedFieldIds(selectionsApi.getResolvedSelection());

    if (fieldIds.length === 0) {
      return;
    }

    traceDatabaseAction(
      {
        action: DatabaseTraceAction.FieldMoveRight,
        source: DatabaseTraceSource.ActionBar
      },
      () => {
        dispatch(doMoveFieldsWithIds(contentId, view.settings, false, fieldIds));
      }
    );

    afterNextLayout(() => selectionsApi.selectFields(fieldIds));
  };

  /**
   * Hides all selected fields from the current view.
   */
  const handleHideFields = () => {
    const fieldIds = getSelectedFieldIds(selectionsApi.getResolvedSelection());

    if (fieldIds.length === 0) {
      return;
    }

    const visibleFields = databaseAdapter
      .getVisibleFieldIds()
      .map((id) => databaseAdapter.getFieldById(id))
      .filter((f): f is DatabaseField => f !== undefined);

    fieldIds.forEach((fieldId) => {
      const viewChange: ViewChangeType = {
        type: ViewChangeTypes.VIEW_FIELDS,
        fieldKey: fieldId,
        fieldValue: false,
        fields: visibleFields
      };

      setViewChange(viewChange);

      selectionsApi.deselectAll();
    });
  };

  /**
   * Opens the inline Rovo AI prompt for the selected field.
   */
  const handleAskRovo = () => {
    return openInlineRovoPrompt(InlineRovoPromptTriggerSource.TableField);
  };

  /**
   * Deletes the specified fields after showing a confirmation dialog.
   * Hides the field-settings panel when one of the deleted fields is displayed,
   * dispatches the Redux delete action, and fires analytics.
   */
  const handleDeleteFields: ColumnActionsAPI['handleDeleteFields'] = (
    fieldIds,
    showDialog
  ) => {
    if (fieldIds.length === 0) {
      return;
    }

    const fieldNames = fieldIds
      .map((id) => databaseAdapter.getFieldById(id)?.name)
      .filter((name): name is string => name !== undefined);

    showDialog('ConfirmDialog', {
      heading: formatMessage(messages['field-settings.delete-field']),
      body: (
        <Stack space="space.150">
          <Box>
            {formatMessage(
              messages[
                'field-settings.delete-multiple-fields-confirmation-message.fields'
              ],
              {
                fieldsNumber: fieldNames.length,
                fields: <Text weight="bold">{fieldNames.join(', ')}</Text>
              }
            )}
          </Box>
          <Box>
            {formatMessage(
              messages[
                'field-settings.delete-multiple-fields-confirmation-message.additional'
              ]
            )}
          </Box>
        </Stack>
      ),
      yesLabel: formatMessage(messages['delete-buttons.delete-field-button']),
      noLabel: formatMessage(messages['delete-buttons.delete-field-cancel-button']),
      appearance: 'danger',
      onClose: (result: boolean) => {
        selectionsApi.deselectAll();

        if (result) {
          const reliability = ReliabilityMetrics.getInstance();

          fieldIds.forEach((fieldId) => {
            const deletedField = databaseAdapter.getFieldById(fieldId);
            if (deletedField) {
              void analyticsClient.sendDeleteFieldEvent({
                contentId,
                databaseContainer: getDatabaseContainerType(),
                fieldIdDeleted: deletedField.id,
                fieldName: deletedField.name,
                fieldType: deletedField.type
              });
            }
          });

          traceDatabaseAction(
            {
              action: DatabaseTraceAction.FieldDelete,
              source: DatabaseTraceSource.ActionBar
            },
            () => {
              try {
                dispatch(doDeleteFieldsWithIds(contentId, fieldIds));
                reliability.success(MetricName.DATABASE_DELETE_FIELD);
              } catch (e) {
                const errorMessage = e instanceof Error ? e.message : String(e);
                reliability.failure(MetricName.DATABASE_DELETE_FIELD, {
                  errorMessage
                });
                throw e;
              }
            }
          );
        }
      }
    });
  };

  /**
   * Inserts new fields before or after the selected fields and selects them.
   * When `fieldIds` is provided it is used directly; otherwise the IDs are
   * resolved from the current selection state.
   */
  const handleInsertFields = async (position: PositionType, fieldIds?: string[]) => {
    const ids = fieldIds ?? getSelectedFieldIds(selectionsApi.getResolvedSelection());

    if (ids.length === 0) {
      return;
    }

    const fieldType = getFieldType(DatabaseFieldType.TEXT);
    const newFieldLabel = formatMessage(
      messages['field-settings.field-name-inline-edit-default-text'],
      {
        defaultFieldName: formatMessage(fieldType.label)
      }
    );

    await dispatch(
      doSelectNewFields(contentId, () =>
        traceDatabaseAction(
          {
            action:
              position === 'before'
                ? DatabaseTraceAction.FieldInsertBefore
                : DatabaseTraceAction.FieldInsertAfter,
            source: DatabaseTraceSource.ActionBar
          },
          () => {
            dispatch(
              doInsertFieldsWithIds(
                contentId,
                view.id,
                position === 'before',
                newFieldLabel,
                ids
              )
            );
          }
        )
      )
    );

    await analyticsClient.sendInsertColumnEvent({
      contentId: contentId,
      databaseContainer: getDatabaseContainerType()
    });
  };

  return {
    handleAskRovo,
    handleClearFields,
    handleDeleteFields,
    handleDuplicateFields,
    handleHideFields,
    handleInsertFields,
    handleMoveFieldsLeft,
    handleMoveFieldsRight
  };
};
